import { useState, useCallback, useEffect } from 'react'
import { loadPersistedSet, savePersistedSet } from '@/lib/storage/local-storage'

/**
 * Manages folder tree expanded state and preserved (empty) folders.
 * Preserved folders persist to localStorage so folder structure survives
 * when all shares are moved out.
 */
export function useFolderTree() {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(['/']),
  )

  // Preserved folders - keeps empty folders visible (persisted to localStorage per device)
  const [preservedFolders, setPreservedFolders] = useState<Set<string>>(() =>
    loadPersistedSet('myrkup-preserved-folders'),
  )

  // Persist preserved folders to localStorage
  useEffect(() => {
    savePersistedSet('myrkup-preserved-folders', preservedFolders)
  }, [preservedFolders])

  const toggleFolder = useCallback((path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }, [])

  const deletePreservedFolder = useCallback((path: string) => {
    setPreservedFolders((prev) => {
      const next = new Set(prev)
      next.delete(path)
      return next
    })
  }, [])

  return {
    expandedFolders,
    toggleFolder,
    preservedFolders,
    setPreservedFolders,
    deletePreservedFolder,
  }
}
